import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import {
  CreditCard, Check, Clock, AlertCircle, ArrowUp, ArrowDown,
  XCircle, Loader2, RefreshCw, Calendar, Shield, Sparkles,
  FileText, Download, Receipt
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  trialing: { label: 'Testphase', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  active: { label: 'Aktiv', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Check },
  past_due: { label: 'Zahlungsverzug', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertCircle },
  cancelled: { label: 'Gekündigt', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
  suspended: { label: 'Gesperrt', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Shield },
  expired: { label: 'Abgelaufen', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

export default function Subscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Dialogs
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [downgradeDialog, setDowngradeDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
    fetchInvoices();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch');
      }
      
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      toast.error('Fehler beim Laden des Abos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/billing/invoices`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    setActionLoading('upgrade');
    try {
      const response = await fetch(`${API_URL}/api/subscription/upgrade`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ new_plan: selectedPlan })
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Upgrade fehlgeschlagen');
      
      toast.success('Upgrade erfolgreich!', { description: data.message });
      setUpgradeDialog(false);
      setSelectedPlan(null);
      fetchSubscription();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleDowngrade = async () => {
    if (!selectedPlan) return;
    
    setActionLoading('downgrade');
    try {
      const response = await fetch(`${API_URL}/api/subscription/downgrade`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ new_plan: selectedPlan })
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Downgrade fehlgeschlagen');
      
      toast.success('Downgrade vorgemerkt', { description: data.message });
      setDowngradeDialog(false);
      setSelectedPlan(null);
      fetchSubscription();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleCancel = async () => {
    setActionLoading('cancel');
    try {
      const response = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: cancelReason, feedback: cancelFeedback })
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Kündigung fehlgeschlagen');
      
      toast.success('Abo gekündigt', { description: data.message });
      setCancelDialog(false);
      setCancelReason('');
      setCancelFeedback('');
      fetchSubscription();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      const response = await fetch(`${API_URL}/api/subscription/reactivate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Reaktivierung fehlgeschlagen');
      
      toast.success('Abo reaktiviert!', { description: data.message });
      fetchSubscription();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading('');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPlanIndex = (planId) => {
    const order = ['starter', 'professional', 'enterprise'];
    return order.indexOf(planId);
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <Skeleton className="h-64 w-full bg-slate-700 rounded-lg" />
      </div>
    );
  }

  const config = statusConfig[subscription?.account_status] || statusConfig.active;
  const StatusIcon = config.icon;
  const currentPlanIndex = getPlanIndex(subscription?.plan);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Toaster position="top-center" />
      
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="subscription-title"
        >
          Mein Abo
        </h1>
        <p className="text-slate-400 mt-1">Verwalten Sie Ihr Cash-X Abonnement</p>
      </div>

      {/* Current Subscription Card */}
      <Card 
        className="p-6 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Badge className={`${config.color} border`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {config.label}
              </Badge>
              {subscription?.cancel_at_period_end && (
                <Badge variant="outline" className="border-amber-500 text-amber-400">
                  Kündigung zum Periodenende
                </Badge>
              )}
            </div>
            <h2 className="text-2xl font-bold capitalize">
              {subscription?.plan_details?.name || subscription?.plan}
            </h2>
            <p className="text-3xl font-bold text-[#2F80FF] mt-2">
              {subscription?.plan_details?.price_display || '0€'}
              <span className="text-sm text-slate-400 font-normal"> / Monat</span>
            </p>
          </div>
          
          <div className="text-right">
            {subscription?.days_remaining !== null && (
              <div className="mb-2">
                <p className="text-sm text-slate-400">
                  {subscription?.account_status === 'trialing' ? 'Trial endet in' : 'Nächste Abrechnung in'}
                </p>
                <p className="text-2xl font-bold">
                  {subscription?.days_remaining} Tagen
                </p>
              </div>
            )}
            {subscription?.current_period_end && (
              <p className="text-sm text-slate-400 flex items-center justify-end gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(subscription.current_period_end)}
              </p>
            )}
          </div>
        </div>

        {/* Trial Progress */}
        {subscription?.account_status === 'trialing' && subscription?.days_remaining !== null && subscription?.days_remaining >= 0 && (
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Trial-Fortschritt</span>
              <span className="text-blue-400">{Math.max(0, 14 - subscription.days_remaining)} / 14 Tage</span>
            </div>
            <Progress 
              value={Math.min(100, Math.max(0, ((14 - subscription.days_remaining) / 14) * 100))} 
              className="h-2 bg-slate-700" 
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-700">
          {subscription?.account_status === 'cancelled' ? (
            <Button
              onClick={handleReactivate}
              disabled={actionLoading === 'reactivate'}
              data-testid="reactivate-btn"
              className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
            >
              {actionLoading === 'reactivate' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Abo reaktivieren
            </Button>
          ) : (
            <>
              {subscription?.can_upgrade && (
                <Button
                  onClick={() => setUpgradeDialog(true)}
                  data-testid="upgrade-btn"
                  className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
                >
                  <ArrowUp className="h-4 w-4 mr-2" />
                  Upgrade
                </Button>
              )}
              {subscription?.can_downgrade && subscription?.account_status !== 'trialing' && (
                <Button
                  variant="outline"
                  onClick={() => setDowngradeDialog(true)}
                  data-testid="downgrade-btn"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <ArrowDown className="h-4 w-4 mr-2" />
                  Downgrade
                </Button>
              )}
              {!['suspended', 'expired', 'cancelled'].includes(subscription?.account_status) && (
                <Button
                  variant="ghost"
                  onClick={() => setCancelDialog(true)}
                  data-testid="cancel-btn"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Kündigen
                </Button>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Plan Features */}
      <Card 
        className="p-6 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <h3 className="text-lg font-semibold mb-4">Ihre Plan-Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subscription?.plan_details?.features && Object.entries(subscription.plan_details.features).map(([key, value]) => {
            const labels = {
              pos_terminals: 'Kassenplätze',
              tse_signatures_per_month: 'TSE-Signaturen/Monat',
              locations: 'Standorte',
              datev_export: 'DATEV-Export',
              table_management: 'Tischverwaltung',
              api_access: 'API-Zugang',
              support_level: 'Support'
            };
            
            const formatValue = (v) => {
              if (v === true) return 'Inklusive';
              if (v === false) return 'Nicht verfügbar';
              if (v === -1) return 'Unbegrenzt';
              if (key === 'tse_signatures_per_month') return v.toLocaleString('de-DE');
              if (key === 'support_level') {
                const supportLabels = { email: 'E-Mail', email_chat: 'E-Mail & Chat', priority: 'Priority' };
                return supportLabels[v] || v;
              }
              return v;
            };
            
            return (
              <div 
                key={key}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: '#0E1628' }}
              >
                <span className="text-slate-400">{labels[key] || key}</span>
                <span className={`font-medium ${
                  value === false ? 'text-slate-500' : 
                  value === true || value === -1 ? 'text-emerald-400' : 'text-white'
                }`}>
                  {value === true && <Check className="h-4 w-4 inline mr-1" />}
                  {formatValue(value)}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Invoices/Rechnungen */}
      <Card 
        className="p-6 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        data-testid="invoices-section"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[#2F80FF]" />
            Rechnungsübersicht
          </h3>
          <Badge variant="outline" className="border-slate-600 text-slate-400">
            MOCK - Demo-Daten
          </Badge>
        </div>
        
        {invoicesLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#2F80FF]" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Noch keine Rechnungen vorhanden</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Rechnungs-Nr.</TableHead>
                <TableHead className="text-slate-400">Zeitraum</TableHead>
                <TableHead className="text-slate-400">Betrag</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => {
                const periodStart = invoice.period_start ? new Date(invoice.period_start) : null;
                const periodEnd = invoice.period_end ? new Date(invoice.period_end) : null;
                const periodText = periodStart && periodEnd 
                  ? `${periodStart.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}`
                  : '-';
                
                const statusConfig = {
                  paid: { label: 'Bezahlt', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
                  pending: { label: 'Offen', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
                  unpaid: { label: 'Unbezahlt', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
                  cancelled: { label: 'Storniert', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
                };
                const status = statusConfig[invoice.status] || statusConfig.pending;
                
                return (
                  <TableRow 
                    key={invoice.invoice_id} 
                    className="border-slate-700/50 hover:bg-slate-800/30"
                    data-testid={`invoice-row-${invoice.invoice_id}`}
                  >
                    <TableCell className="font-mono text-sm text-white">
                      {invoice.invoice_id}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {periodText}
                    </TableCell>
                    <TableCell className="text-white font-medium">
                      €{(invoice.amount_eur || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.className} border`}>
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-[#2F80FF] hover:text-[#4A92FF] hover:bg-[#2F80FF]/10"
                        onClick={() => toast.info('PDF-Download ist in der Demo-Version deaktiviert')}
                        data-testid={`download-invoice-${invoice.invoice_id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Available Plans */}
      <h3 className="text-lg font-semibold mb-4">Alle Pläne</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const isCurrent = plan.id === subscription?.plan;
          const planIndex = getPlanIndex(plan.id);
          const canUpgradeTo = planIndex > currentPlanIndex;
          const canDowngradeTo = planIndex < currentPlanIndex && subscription?.account_status !== 'trialing';
          
          return (
            <Card 
              key={plan.id}
              className={`p-6 relative ${
                isCurrent ? 'ring-2 ring-[#2F80FF]' : ''
              }`}
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
              data-testid={`plan-card-${plan.id}`}
            >
              {isCurrent && (
                <Badge className="absolute -top-2 -right-2 bg-[#2F80FF] text-white">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Aktuell
                </Badge>
              )}
              
              <h4 className="text-xl font-bold capitalize">{plan.name}</h4>
              <p className="text-3xl font-bold mt-2">
                {plan.price_display}
                <span className="text-sm text-slate-400 font-normal"> / Monat</span>
              </p>
              
              <div className="mt-4 space-y-2">
                {Object.entries(plan.features).slice(0, 4).map(([key, value]) => {
                  const labels = {
                    pos_terminals: 'Kassenplätze',
                    tse_signatures_per_month: 'TSE-Sign.',
                    datev_export: 'DATEV',
                    table_management: 'Tische'
                  };
                  
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      {value === false ? (
                        <XCircle className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Check className="h-4 w-4 text-emerald-400" />
                      )}
                      <span className={value === false ? 'text-slate-500' : 'text-slate-300'}>
                        {labels[key] || key}: {value === true ? 'Ja' : value === false ? 'Nein' : value === -1 ? '∞' : value}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4">
                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline">
                    Aktueller Plan
                  </Button>
                ) : canUpgradeTo ? (
                  <Button
                    className="w-full bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
                    onClick={() => { setSelectedPlan(plan.id); setUpgradeDialog(true); }}
                  >
                    <ArrowUp className="h-4 w-4 mr-2" />
                    Upgrade
                  </Button>
                ) : canDowngradeTo ? (
                  <Button
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                    onClick={() => { setSelectedPlan(plan.id); setDowngradeDialog(true); }}
                  >
                    <ArrowDown className="h-4 w-4 mr-2" />
                    Downgrade
                  </Button>
                ) : (
                  <Button disabled className="w-full" variant="ghost">
                    Nicht verfügbar
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialog} onOpenChange={setUpgradeDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUp className="h-5 w-5 text-[#2F80FF]" />
              Plan upgraden
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Ihr Upgrade wird sofort aktiviert. Die Differenz wird anteilig berechnet.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-lg bg-[#2F80FF]/10 border border-[#2F80FF]/30">
            <p className="text-sm text-[#2F80FF]">
              Sie wechseln zu: <span className="font-bold capitalize">{selectedPlan}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpgradeDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleUpgrade}
              disabled={actionLoading === 'upgrade'}
              className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
            >
              {actionLoading === 'upgrade' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4 mr-2" />
              )}
              Jetzt upgraden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Downgrade Dialog */}
      <Dialog open={downgradeDialog} onOpenChange={setDowngradeDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDown className="h-5 w-5 text-amber-400" />
              Plan downgraden
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Das Downgrade wird zum Ende Ihrer aktuellen Abrechnungsperiode wirksam.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-400">
              Sie wechseln zu: <span className="font-bold capitalize">{selectedPlan}</span>
              <br />
              <span className="text-slate-400">Wirksam ab: {formatDate(subscription?.current_period_end)}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDowngradeDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleDowngrade}
              disabled={actionLoading === 'downgrade'}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {actionLoading === 'downgrade' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowDown className="h-4 w-4 mr-2" />
              )}
              Downgrade vormerken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              Abo kündigen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Schade, dass Sie gehen möchten. Ihr Zugang bleibt bis zum Periodenende aktiv.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Warum kündigen Sie? (optional)</label>
              <Textarea
                placeholder="z.B. zu teuer, fehlende Features, ..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-1 bg-[#0E1628] border-slate-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Was könnten wir verbessern? (optional)</label>
              <Textarea
                placeholder="Ihr Feedback hilft uns..."
                value={cancelFeedback}
                onChange={(e) => setCancelFeedback(e.target.value)}
                className="mt-1 bg-[#0E1628] border-slate-700 text-white"
              />
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-slate-800/50">
            <p className="text-sm text-slate-400">
              Zugang aktiv bis: <span className="text-white">{formatDate(subscription?.current_period_end)}</span>
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCancel}
              disabled={actionLoading === 'cancel'}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {actionLoading === 'cancel' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Abo kündigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
